// components/Loader.js
import React from 'react';
import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const LoaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background: ${props => props.theme?.background || '#f8f9fa'};
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
`;

const SpinnerWrapper = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 30px;
`;

const SpinnerRing = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid transparent;
  border-top-color: ${props => props.theme?.primary || '#4f46e5'};
  border-right-color: ${props => props.theme?.primary || '#4f46e5'};
  animation: ${spin} 1s linear infinite;
  
  &:nth-child(2) {
    width: 70%;
    height: 70%;
    top: 15%;
    left: 15%;
    border-top-color: ${props => props.theme?.secondary || '#7c3aed'};
    border-right-color: ${props => props.theme?.secondary || '#7c3aed'};
    animation-duration: 0.8s;
    animation-direction: reverse;
  }
  
  &:nth-child(3) {
    width: 40%;
    height: 40%;
    top: 30%;
    left: 30%;
    border-top-color: ${props => props.theme?.accent || '#06b6d4'};
    border-right-color: ${props => props.theme?.accent || '#06b6d4'};
    animation-duration: 0.6s;
  }
`;

const LoaderText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.theme?.text || '#374151'};
  animation: ${pulse} 1.5s ease-in-out infinite;
  letter-spacing: 0.5px;
`;

const LoaderSubText = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${props => props.theme?.textSecondary || '#9ca3af'};
  margin-top: 8px;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 3px;
  background: ${props => props.theme?.border || '#e5e7eb'};
  border-radius: 2px;
  margin-top: 20px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${props => props.theme?.primary || '#4f46e5'}, ${props => props.theme?.secondary || '#7c3aed'});
  border-radius: 2px;
  animation: ${spin} 2s ease-in-out infinite;
  width: 30%;
  position: absolute;
  left: -30%;
`;

const DotContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.theme?.primary || '#4f46e5'};
  animation: ${pulse} 1.5s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
`;

export const Loader = ({ text = 'Loading...', subText = 'Please wait while we prepare your workspace' }) => {
  return (
    <LoaderContainer>
      <SpinnerWrapper>
        <SpinnerRing />
        <SpinnerRing />
        <SpinnerRing />
      </SpinnerWrapper>
      <LoaderText>{text}</LoaderText>
      <LoaderSubText>{subText}</LoaderSubText>
      <ProgressBar>
        <ProgressFill />
      </ProgressBar>
      <DotContainer>
        <Dot delay="0s" />
        <Dot delay="0.2s" />
        <Dot delay="0.4s" />
        <Dot delay="0.6s" />
        <Dot delay="0.8s" />
      </DotContainer>
    </LoaderContainer>
  );
};

// Minimal version for inline loading
export const InlineLoader = ({ size = 40, color = '#4f46e5' }) => {
  const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;

  const Spinner = styled.div`
    width: ${size}px;
    height: ${size}px;
    border: 3px solid ${props => props.color || color};
    border-radius: 50%;
    border-top-color: transparent;
    animation: ${spin} 0.8s linear infinite;
  `;

  return <Spinner color={color} />;
};

// Skeleton loader for content
export const SkeletonLoader = ({ type = 'card' }) => {
  const skeletonKeyframes = keyframes`
    0% { background-position: -200px 0; }
    100% { background-position: calc(200px + 100%) 0; }
  `;

  const SkeletonBase = styled.div`
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: ${skeletonKeyframes} 1.5s ease-in-out infinite;
    border-radius: 4px;
  `;

  if (type === 'card') {
    return (
      <div style={{ padding: '20px', background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <SkeletonBase style={{ height: '20px', width: '60%', marginBottom: '15px' }} />
        <SkeletonBase style={{ height: '100px', width: '100%', marginBottom: '10px' }} />
        <SkeletonBase style={{ height: '12px', width: '80%', marginBottom: '8px' }} />
        <SkeletonBase style={{ height: '12px', width: '70%' }} />
      </div>
    );
  }

  return null;
};